import type {
  ReplayRequest,
  ReplayResponse,
  ReplaySendOptions,
} from '../types/replay';

export const MAX_AUTO_REDIRECTS = 10;

export function isRedirectStatus(statusCode: number): boolean {
  return (
    statusCode === 301 ||
    statusCode === 302 ||
    statusCode === 303 ||
    statusCode === 307 ||
    statusCode === 308
  );
}

export function resolveRedirectUrl({
  redirectUrl,
  currentUrl,
}: {
  redirectUrl: string;
  currentUrl: string;
}): string {
  try {
    return new URL(redirectUrl, currentUrl).href;
  } catch {
    return redirectUrl;
  }
}

export type RedirectableRequest = {
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  body: string;
  httpVersion?: string;
};

export function buildRedirectRequest({
  currentRequest,
  redirectUrl,
  statusCode,
}: {
  currentRequest: RedirectableRequest;
  redirectUrl: string;
  statusCode: number;
}): RedirectableRequest {
  const fullRedirectUrl = resolveRedirectUrl({
    redirectUrl,
    currentUrl: currentRequest.url,
  });
  const shouldPreserveMethod = statusCode === 307 || statusCode === 308;
  const newMethod = shouldPreserveMethod ? currentRequest.method : 'GET';

  let newHost = '';
  try {
    newHost = new URL(fullRedirectUrl).host;
  } catch {
    try {
      newHost = new URL(currentRequest.url).host;
    } catch {
      newHost = '';
    }
  }

  const newHeaders = { ...currentRequest.headers };
  if (newHost) {
    for (const key of Object.keys(newHeaders)) {
      if (key.toLowerCase() === 'host') {
        delete newHeaders[key];
      }
    }
    newHeaders.Host = newHost;
  }

  return {
    method: newMethod,
    url: fullRedirectUrl,
    headers: newHeaders,
    body: shouldPreserveMethod ? currentRequest.body : '',
    httpVersion: currentRequest.httpVersion,
  };
}

export function getLocationHeader(
  headers: Record<string, string | string[]>,
): string | null {
  const entry = Object.entries(headers).find(
    ([key]) => key.toLowerCase() === 'location',
  );
  if (!entry) {
    return null;
  }

  const value = entry[1];
  return Array.isArray(value) ? value[0] || null : value || null;
}

export async function sendReplayRequestWithRedirects({
  initialRequest,
  sendReplayRequest,
  followRedirects = false,
  maxRedirects = MAX_AUTO_REDIRECTS,
  sendOptions,
}: {
  initialRequest: ReplayRequest;
  sendReplayRequest: (
    request: ReplayRequest,
    options?: ReplaySendOptions,
  ) => Promise<ReplayResponse>;
  followRedirects?: boolean;
  maxRedirects?: number;
  sendOptions?: ReplaySendOptions;
}): Promise<ReplayResponse> {
  let currentRequest = initialRequest;
  let response = await sendReplayRequest(currentRequest, sendOptions);

  if (!followRedirects) {
    return response;
  }

  let redirectCount = 0;
  while (
    isRedirectStatus(response.statusCode) &&
    !response.error &&
    redirectCount < maxRedirects
  ) {
    const locationUrl = getLocationHeader(response.headers);
    if (!locationUrl) {
      break;
    }

    const redirected = buildRedirectRequest({
      currentRequest: {
        method: currentRequest.method,
        url: currentRequest.url,
        headers: currentRequest.headers,
        body: currentRequest.body,
        httpVersion: response.httpVersion,
      },
      redirectUrl: locationUrl,
      statusCode: response.statusCode,
    });

    currentRequest = {
      ...currentRequest,
      method: redirected.method,
      url: redirected.url,
      headers: redirected.headers,
      body: redirected.body,
    };

    response = await sendReplayRequest(currentRequest, sendOptions);
    redirectCount += 1;
  }

  return response;
}
